import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Container, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import Select from "react-select";
import { Upload } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { loadingFalse, loadingTrue } from "../../Reducer/loaderSlice";
import { useDispatch, useSelector } from "react-redux";

const EditProduct = () => {

    const { id } = useParams();
    const dispatch = useDispatch();
    const loading = useSelector((state) => state?.loading?.loading);
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        name: "",
        price: "",
        unit: "",
        category: null,
        subcategory: null,
        image: null,
        old_image: "",
    });

    const [preview, setPreview] = useState(null);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    /* ---------------- API CALLS ---------------- */

    const getCategories = async () => {
        const response = await Handler({ method: "get", url: `/categories/categories_list.php` });

        if (response?.success) {
            setCategories(
                response.data.map(c => ({
                    value: c.category_id,
                    label: c.name
                }))
            );
        }
    };

    const getSubcategories = async (categoryName) => {
        if (!categoryName) return;

        const response = await Handler({
            method: "get",
            url: `/categories/sub_categories_list.php?category_name=${categoryName}`,
        });

        if (response?.success) {
            setSubcategories(
                response.data.map(s => ({
                    value: s.subcategory_id,
                    label: s.name
                }))
            );
        }
    };

    const getProductById = async () => {
        const response = await Handler({
            method: "get",
            url: `/products/view.php?product_id=${id}`,
        });
        if (response?.success && response.data) {
            const p = response.data;

            setFormData({
                name: p.name,
                price: Number(p.price),
                unit: p.unit,
                category: p.category
                    ? { value: p.category_id, label: p.category }
                    : null,
                subcategory: p.subcategory
                    ? { value: p.subcategory_id, label: p.subcategory }
                    : null,
                image: null,
                old_image: p.image,
            });

            setPreview(`https://bizcarto.in/demoadmin/api/${p.image}`);

            getSubcategories(p.category);
        }
    };

    useEffect(() => {
        getCategories();
        getProductById();
    }, []);

    /* ---------------- IMAGE SELECT ---------------- */

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                return toast.error("Image must be below 2MB");
            }

            setFormData(prev => ({ ...prev, image: file }));
            setPreview(URL.createObjectURL(file));
        }
    };

    /* ---------------- FORM SUBMIT ---------------- */

    const handleSubmit = async () => {
        const { name, price, unit, category, subcategory } = formData;

        if (!name) return toast.error("Enter product name");
        if (!price) return toast.error("Enter price");
        if (!unit) return toast.error("Enter unit");
        if (!category) return toast.error("Select category");
        if (!subcategory) return toast.error("Select subcategory");

        const payload = new FormData();
        payload.append("product_id", id);
        payload.append("name", name);
        payload.append("price", price);
        payload.append("unit", unit);
        payload.append("category_name", category.label);
        payload.append("subcategory_name", subcategory.label);

        // ✔ New image (optional)
        if (formData.image) {
            payload.append("image", formData.image);
        }

        // ✔ Always send old image safely
        payload.append("old_image", formData.old_image);
        dispatch(loadingTrue())
        const response = await Handler({
            method: "post", url: `/products/update.php`, data: payload, headers: { "Content-Type": "multipart/form-data" },
        });

        if (response?.success) {
            dispatch(loadingFalse())
            toast.success("Product updated successfully");
            setTimeout(() => {
                navigate("/products");
            }, 1000)
        } else {
            dispatch(loadingFalse())
            toast.error(response.msg || "Failed to update");
        }
    };

    /* ---------------- CATEGORY CHANGE ---------------- */

    const handleCategoryChange = (opt) => {
        setFormData({
            ...formData,
            category: opt,
            subcategory: null,
        });

        getSubcategories(opt.label);
    };

    return (
        <Container>
            <Row className="py-5 justify-content-center">
                <Col md={10}>
                    <h2 className="category-title text-center mb-4">Edit Product</h2>
                    <Row>
                        <Col md={6}>
                            <label>Product Image</label>

                            <div className="upload-section" onClick={() => fileInputRef.current.click()}>
                                {preview ? (
                                    <img src={preview} className="preview-image" alt="preview" />
                                ) : (
                                    <div className="upload-placeholder">
                                        <Upload size={40} color="#FE9F43" />
                                        <p>Upload Image</p>
                                        <small>Max 2MB</small>
                                    </div>
                                )}
                            </div>

                            <input
                                hidden
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                onChange={handleImageSelect}
                            />

                            <FormGroup className="mt-3">
                                <Label>Name</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) =>
                                        setFormData({ ...formData, name: e.target.value })
                                    }
                                />
                            </FormGroup>
                        </Col>

                        <Col md={6}>
                            <FormGroup>
                                <Label>Price</Label>
                                <Input
                                    value={formData.price}
                                    onChange={(e) => {
                                        if (/^\d*$/.test(e.target.value)) {
                                            setFormData({ ...formData, price: e.target.value });
                                        }
                                    }}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Unit</Label>
                                <Input
                                    value={formData.unit}
                                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Category</Label>
                                <Select
                                    classNamePrefix="vertical"
                                    options={categories}
                                    value={formData.category}
                                    onChange={handleCategoryChange}
                                    placeholder="Select Category"
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Sub Category</Label>
                                <Select
                                    classNamePrefix="vertical"
                                    options={subcategories}
                                    value={formData.subcategory}
                                    onChange={(opt) =>
                                        setFormData({ ...formData, subcategory: opt })
                                    }
                                    isDisabled={!formData.category}
                                    placeholder="Select Subcategory"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Button color="primary" disabled={loading} onClick={handleSubmit} className="d-flex align-items-center justify-content-center gap-2 submit-btn mt-3">
                        {loading && (
                            <Spinner size="sm" color="light" />
                        )}
                        Update Product
                    </Button>
                    {/* <button className="submit-btn mt-3" onClick={handleSubmit}>
                        Update Product
                    </button> */}
                </Col>
            </Row>
        </Container>
    );
};

export default EditProduct;
